import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import MenuItem from '@material-ui/core/MenuItem';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import { reduxForm, Field, SubmissionError } from 'redux-form';

import ORTypography from 'components/ORTypography/ORTypography';
import { required, maxLength, email } from 'services/validationMsgs';
import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import CollapsibleCard from '../ProfileCard/CollapsibleCard/CollapsibleCard';

const maxLength128 = maxLength(128);

const propTypes = {
  org: PropTypes.shape({
    companyName: PropTypes.string.isRequired,
    primaryEmail: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string,
  }),
  className: PropTypes.string,
  classes: PropTypes.shape({
    cardSubheader: PropTypes.string.isRequired,
  }).isRequired,
  updateOrg: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
};

const defaultProps = {
  className: null,
  org: {
    phoneNumber: '',
  },
};

export function UnstyledOrganizationCard({ org, className, classes, updateOrg, handleSubmit }) {
  const handleFormSubmissionReturn = (result) => {
    if (result.error) {
      throw new SubmissionError(result.formErrors);
    }
  };

  const submitHandler = (data) => updateOrg(data).then(handleFormSubmissionReturn);

  return (
    <CollapsibleCard
      className={className}
      headerProps={{
        title: 'Organization',
        className: classes.content,
        classes: {
          subheader: classes.cardSubheader,
        },
        subheaderTypographyProps: {
          color: 'textPrimary',
        },
      }}
    >
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className={classes.fieldContainer}>
          <Field
            variant="outlined"
            name="organization"
            label="Organization"
            placeholder={org.companyName}
            component={WrappedTextField}
            fullWidth
            disabled
            validate={[maxLength128, required]}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <Field
            variant="outlined"
            name="email"
            label="Email"
            placeholder={org.primaryEmail}
            component={WrappedTextField}
            fullWidth
            disabled
            validate={[maxLength128, required, email]}
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
          />
          <div className={classes.addressFieldContainer}>
            <ORTypography variant="subtitle2">Billing Address</ORTypography>
            <div className={classes.streetAddress}>
              <Field
                variant="outlined"
                name="address"
                label="Address"
                placeholder="Address Line 1"
                component={WrappedTextField}
                disabled
                validate={[maxLength128, required]}
                InputLabelProps={{
                  shrink: true,
                }}
                className={classes.street}
              />
              <Field
                variant="outlined"
                name="suite"
                label="Suite"
                placeholder="Suite"
                component={WrappedTextField}
                disabled
                validate={[maxLength128, required]}
                InputLabelProps={{
                  shrink: true,
                }}
                className={classes.suite}
              />
            </div>
            <Field
              variant="outlined"
              name="city"
              label="City"
              placeholder="City"
              component={WrappedTextField}
              fullWidth
              disabled
              validate={[maxLength128, required]}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <div className={classes.stateAndZip}>
              <Field
                variant="outlined"
                name="state"
                label="State"
                placeholder="State"
                component={WrappedTextField}
                disabled
                select
                validate={[maxLength128, required]}
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  displayEmpty: true,
                  IconComponent: KeyboardArrowDown,
                  value: '',
                  classes: { root: classes.disabledSelectText },
                }}
                className={classes.state}
              >
                <MenuItem value="" disabled>
                  State
                </MenuItem>
              </Field>
              <Field
                variant="outlined"
                name="zip"
                label="Zip Code"
                placeholder="Zip Code"
                component={WrappedTextField}
                disabled
                validate={[maxLength128, required]}
                InputLabelProps={{
                  shrink: true,
                }}
                className={classes.zip}
              />
            </div>
          </div>
          <Field
            variant="outlined"
            name="phone"
            label="Phone Number"
            placeholder={org.phoneNumber || 'Phone Number'}
            component={WrappedTextField}
            fullWidth
            disabled
            validate={[maxLength128, required]}
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </div>
      </form>
    </CollapsibleCard>
  );
}

UnstyledOrganizationCard.propTypes = propTypes;
UnstyledOrganizationCard.defaultProps = defaultProps;

const styles = (theme) => ({
  content: {
    marginBottom: '-4px',
  },
  cardSubheader: {
    textTransform: 'capitalize',
    marginBottom: '5px',
  },
  fieldContainer: {
    marginTop: theme.spacing(1),
    '& input': {
      textAlign: 'left',
    },
    '& label': {
      color: '#231f20 !important',
    },
  },
  addressFieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    '& input': {
      textAlign: 'left',
    },
    '& > *': {
      marginBottom: theme.spacing(2),
    },
    marginTop: theme.spacing(1),
  },
  streetAddress: {
    display: 'Flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  street: {
    flex: '1 1 75%',
  },
  suite: {
    marginLeft: theme.spacing(1),
    flex: '2 1 25%',
  },
  stateAndZip: {
    display: 'Flex',
    flexDirection: 'row',
  },
  state: {
    flex: '1 1 60%',
  },
  zip: {
    marginLeft: theme.spacing(1),
    flex: '2 1 40%',
  },
  disabledSelectText: {
    color: 'rgba(219, 219, 219, 1)',
  },
});

export default withStyles(styles)(reduxForm({ form: 'orgInfo' })(UnstyledOrganizationCard));
